import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RRule, rrulestr, Options } from 'rrule';
import { fromZonedTime } from "date-fns-tz";
import { wallCarrier } from "../../../../utilities/calendar/calendarDisplayTime";
import CustomDropdown from '../CustomDropdown';
import { Repeat2 } from "lucide-react";

interface RecurrenceSelectorProps {
  value?: string;
  onChange: (rruleString?: string) => void;
  startTime: string;
  timeZone: string;
}

const frequencyOptions = [
  { label: 'Does not repeat', value: 'NONE' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
];

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({ value, onChange, startTime, timeZone }) => {
  const [selectedFreq, setSelectedFreq] = useState<string>('NONE');

  // Use a ref to prevent cyclic updates when the component itself triggers the change
  const isInternalUpdate = useRef(false);
  const previousSchedule = useRef({ startTime, timeZone });

  // 1. Sync value -> state ONLY when it comes from outside (the database)
  useEffect(() => {
    if (isInternalUpdate.current) {
        isInternalUpdate.current = false;
        return;
    }

    if (value) {
      try {
        const parsed = rrulestr(value);
        let rrule: any = parsed;

        // Safely unwrap the underlying RRule from the RRuleSet
        if (typeof (parsed as any).rrules === 'function') {
            rrule = (parsed as any).rrules()[0];
        }

        if (rrule && rrule.options) {
            if (rrule.options.freq === RRule.DAILY) {
              setSelectedFreq('DAILY');
            } else if (rrule.options.freq === RRule.WEEKLY) {
              setSelectedFreq('WEEKLY');
            } else if (rrule.options.freq === RRule.MONTHLY) {
              setSelectedFreq('MONTHLY');
            } else {
              setSelectedFreq('NONE');
            }
        } else {
            setSelectedFreq('NONE');
        }
      } catch (e) {
        console.error("Error parsing existing RRULE:", e);
        setSelectedFreq('NONE');
      }
    } else {
      setSelectedFreq('NONE');
    }
  }, [value]);

  // 2. Generate string helper
  const generateRRuleString = useCallback((freq: string) => {
    if (freq === 'NONE' || !startTime) return undefined;

    const instant = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(startTime) ? startTime : fromZonedTime(startTime, timeZone);
    const rruleDtStart = wallCarrier(instant, timeZone);

    let rruleOptions: Partial<Options> = {
      dtstart: rruleDtStart,
      tzid: timeZone,
      interval: 1
    };

    const rruleDays = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

    if (freq === 'DAILY') {
      rruleOptions.freq = RRule.DAILY;
    } else if (freq === 'WEEKLY') {
      rruleOptions.freq = RRule.WEEKLY;
      rruleOptions.byweekday = [rruleDays[rruleDtStart.getUTCDay()]] as any;
    } else if (freq === 'MONTHLY') {
      rruleOptions.freq = RRule.MONTHLY;
      rruleOptions.bymonthday = [rruleDtStart.getUTCDate()];
    }

    try {
      return new RRule(rruleOptions as Options).toString();
    } catch (e) {
      console.error("Error generating RRULE:", e);
      return undefined;
    }
  }, [startTime, timeZone]);

  // 3. Handle User Interaction Directly (Breaks the cyclic useEffect loop)
  const handleMainFreqChange = (newVal: string | number) => {
    const stringValue = String(newVal);
    // A gap/fold draft has no preview instant. Keep the last valid rule and
    // selection until time validation succeeds; NONE is an explicit clear.
    if (stringValue !== 'NONE' && !startTime) return;
    setSelectedFreq(stringValue);
    isInternalUpdate.current = true;
    onChange(generateRRuleString(stringValue));
  };

  // 4. Update Rule if Start Time Changes (but keep frequency the same)
  useEffect(() => {
    const previous = previousSchedule.current;
    previousSchedule.current = { startTime, timeZone };
    if (previous.startTime === startTime && previous.timeZone === timeZone) return;
    if (startTime && selectedFreq !== 'NONE' && !isInternalUpdate.current) {
        isInternalUpdate.current = true;
        onChange(generateRRuleString(selectedFreq));
    }
  }, [startTime, timeZone, selectedFreq, generateRRuleString, onChange]);

  return (
    <div className="recurrence-selector">
      <CustomDropdown
        label="Repeat"
        name="repeat"
        value={selectedFreq}
        onChange={handleMainFreqChange}
        options={frequencyOptions}
        icon={<Repeat2 size={18} />}
        isSearchable={false}
        overlay={true}
        custome={true}
      />
    </div>
  );
};

export default RecurrenceSelector;
